import React, { memo, useEffect, useRef, useState, useCallback } from "react";
import { Autocomplete } from "@react-google-maps/api";
import clsx from "clsx";
import { FieldError } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { getClassByVariant } from "helpers/getClassByVariant";
import { extractAddressDetails } from "helpers/googleMap";
import { showErrorMessage } from "helpers/toastMessage";
import { AddressInfo } from "models/User";

import styles from "./style.module.scss";
import "./_style.scss";

interface IProps {
  value?: AddressInfo | null;
  onChange?: (value: AddressInfo | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: FieldError;
  label?: string | React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  color?: "primary" | "secondary";
  isHideError?: boolean;
  containerClass?: string;
}

const GoogleAutoComplete: React.FC<IProps> = ({
  label,
  placeholder = "",
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  color = "primary",
  isHideError = false,
  containerClass
}) => {
  const uniqueId = useRef(uuidv4()).current;
  const [inputValue, setInputValue] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const isSelectingPlace = useRef(false);

  useEffect(() => {
    if (value) {
      const { formattedAddress, address, street_one, city, state, zip, country } = value;
      setInputValue(
        formattedAddress ||
          [address ?? street_one, city, state, zip, country].filter(Boolean).join(", ")
      );
    } else {
      setInputValue("");
    }
  }, [value]);

  const onPlaceChanged = useCallback(() => {
    const ac = autocompleteRef.current;
    if (!ac) {
      showErrorMessage("Autocomplete not ready");
      return;
    }
    const place = ac.getPlace();
    if (!place.geometry?.location) {
      showErrorMessage("Please select a valid address");
      return;
    }
    const extracted = extractAddressDetails(place);
    isSelectingPlace.current = true;
    setInputValue(extracted.formattedAddress);
    onChange?.(extracted);
  }, [onChange]);

  const onLoad = useCallback(
    (ac: google.maps.places.Autocomplete) => {
      autocompleteRef.current = ac;
      ac.setFields(["address_components", "geometry", "formatted_address"]);
      ac.addListener("place_changed", onPlaceChanged);
    },
    [onPlaceChanged]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isSelectingPlace.current) {
        isSelectingPlace.current = false;
        return;
      }
      const v = e.target.value;
      setInputValue(v);
      if (!v.trim()) {
        onChange?.(null);
      }
    },
    [onChange]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
    },
    [onBlur]
  );

  return (
    <div className={clsx(styles.googleAutocompleteContainer, containerClass)}>
      {label && (
        <label
          htmlFor={`google-autocomplete-${uniqueId}`}
          className={clsx(styles.googleAutocompleteLabel, getClassByVariant(color)?.label, {
            "cursor-not-allowed": disabled
          })}>
          {label}
        </label>
      )}

      <Autocomplete
        onLoad={onLoad}
        options={{
          componentRestrictions: { country: ["us", "ca", "mx"] }
        }}>
        <div className={styles.googleAutocompleteInputWrapper}>
          <input
            id={`google-autocomplete-${uniqueId}`}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            className={clsx(
              styles.googleAutocompleteInput,
              getClassByVariant(color)?.input,
              !!error && "!border-b-error"
            )}
            aria-label={typeof label === "string" ? label : "Address input"}
            aria-invalid={!!error}
            aria-describedby={error ? `address-error-${uniqueId}` : undefined}
          />
        </div>
      </Autocomplete>

      {error && !isHideError && (
        <p id={`address-error-${uniqueId}`} className={styles.googleAutocompleteError}>
          {error.message}
        </p>
      )}
    </div>
  );
};

export default memo(GoogleAutoComplete);
